import { Upload, X } from "lucide-react";
import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { toastApiError } from "@/api/errors";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useUploadDocument } from "@/features/knowledge-bases/hooks/useDocuments";
import { formatBytes, validateFile } from "@/lib/upload";

const MAX_CONCURRENT_UPLOADS = 3;

interface UploadItem {
  id: string;
  file: File;
  progress: number;
  status: "queued" | "uploading" | "done" | "error";
  error?: string;
}

interface Props {
  kbId: string;
}

export function DocumentUploader({ kbId }: Props) {
  const { t } = useTranslation("knowledgeBases");
  const upload = useUploadDocument(kbId);
  const inputRef = useRef<HTMLInputElement>(null);
  const [items, setItems] = useState<UploadItem[]>([]);
  const [dragOver, setDragOver] = useState(false);

  const startQueue = useCallback(
    async (newItems: UploadItem[]) => {
      const concurrent: Promise<void>[] = [];
      const queue = [...newItems];

      const runOne = async (item: UploadItem) => {
        setItems((prev) =>
          prev.map((it) =>
            it.id === item.id ? { ...it, status: "uploading" } : it,
          ),
        );
        try {
          await upload.mutateAsync({
            file: item.file,
            onProgress: (pct) =>
              setItems((prev) =>
                prev.map((it) =>
                  it.id === item.id ? { ...it, progress: pct } : it,
                ),
              ),
          });
          setItems((prev) =>
            prev.map((it) =>
              it.id === item.id
                ? { ...it, status: "done", progress: 100 }
                : it,
            ),
          );
        } catch (err) {
          const msg =
            (err as { body?: { error?: { message?: string } } })?.body
              ?.error?.message ??
            (typeof (err as { body?: string }).body === "string"
              ? ((err as { body: string }).body as string)
              : t("uploader.uploadFailed"));
          setItems((prev) =>
            prev.map((it) =>
              it.id === item.id
                ? { ...it, status: "error", error: msg }
                : it,
            ),
          );
          toastApiError(err);
        }
      };

      const worker = async () => {
        while (queue.length > 0) {
          const next = queue.shift();
          if (!next) return;
          await runOne(next);
        }
      };
      for (let i = 0; i < MAX_CONCURRENT_UPLOADS; i++) {
        concurrent.push(worker());
      }
      await Promise.all(concurrent);
    },
    [upload, t],
  );

  function onFiles(files: FileList | null) {
    if (!files) return;
    const accepted: UploadItem[] = [];
    for (const file of Array.from(files)) {
      const v = validateFile(file);
      if (!v.ok) {
        toast.error(`${file.name}: ${v.error}`);
        continue;
      }
      accepted.push({
        id: `${file.name}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        file,
        progress: 0,
        status: "queued",
      });
    }
    if (accepted.length === 0) return;
    setItems((prev) => [...prev, ...accepted]);
    void startQueue(accepted);
  }

  function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    onFiles(e.dataTransfer.files);
  }

  function clearDone() {
    setItems((prev) =>
      prev.filter((it) => it.status !== "done" && it.status !== "error"),
    );
  }

  return (
    <div className="space-y-3">
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        className={`rounded-md border-2 border-dashed p-6 text-center transition-colors ${
          dragOver
            ? "border-brand-500 bg-brand-50"
            : "border-line-strong bg-surface-muted"
        }`}
      >
        <Upload className="mx-auto mb-2 h-8 w-8 text-muted-foreground" />
        <p className="text-sm">
          {t("uploader.dropHere")}
          <button
            type="button"
            className="text-primary underline"
            onClick={() => inputRef.current?.click()}
          >
            {t("uploader.clickToSelect")}
          </button>
        </p>
        <p className="text-xs text-muted-foreground">
          {t("uploader.accepted")}
        </p>
        <input
          ref={inputRef}
          type="file"
          multiple
          className="sr-only"
          onChange={(e) => onFiles(e.target.files)}
          aria-label={t("uploader.selectAria")}
        />
      </div>

      {items.length > 0 ? (
        <div
          className="space-y-2 rounded-md border p-3"
          role="status"
          aria-live="polite"
        >
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>{t("uploader.fileCount", { count: items.length })}</span>
            <Button variant="ghost" size="sm" onClick={clearDone}>
              {t("uploader.clearDone")}
            </Button>
          </div>
          <ul className="space-y-2">
            {items.map((it) => (
              <li key={it.id} className="space-y-1 text-sm">
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate">{it.file.name}</span>
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {formatBytes(it.file.size)}
                  </span>
                </div>
                <Progress value={it.progress} />
                <div className="flex items-center justify-between text-xs">
                  <span
                    className={
                      it.status === "error"
                        ? "text-destructive"
                        : "text-muted-foreground"
                    }
                  >
                    {it.status === "queued" && t("uploader.statusQueued")}
                    {it.status === "uploading" && `${it.progress}%`}
                    {it.status === "done" && t("uploader.statusDone")}
                    {it.status === "error" &&
                      (it.error ?? t("uploader.statusFailed"))}
                  </span>
                  {it.status === "error" ? (
                    <button
                      type="button"
                      className="text-muted-foreground hover:text-foreground"
                      onClick={() =>
                        setItems((prev) =>
                          prev.filter((x) => x.id !== it.id),
                        )
                      }
                      aria-label={t("uploader.removeAria")}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
